import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncSection } from '@/app/components/AsyncSection';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { SubmitError } from '@/app/components/SubmitError';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { exportReport, listReports } from '@/features/reports/api';
import { REPORT_KEYS, type ReportKey, type ReportSummary } from '@/features/reports/types';
import { useMe } from '@/features/me/MeContext';
import { useAsync } from '@/lib/useAsync';

/**
 * `/reports` — the Content pill's Rapportages library (PLAN-FASE3.md
 * WP-04): read-only aggregation over data this API already has (no new
 * datamodel) — the 5 report cards the design-flowchart names
 * (vitaliteitstrends, deelnamerapport, challenge-resultaten,
 * communicatiebereik, documentgebruik), each with a CSV export. No
 * AI-maandsamenvatting card/section — explicitly out of scope per
 * PLAN-FASE3.md "Besluiten", same reasoning as Admin-overzicht's NPS
 * exclusion. Same company-context and permission shape as
 * `DocumentsPage`/`CommunicationsPage`: no active company means nothing to
 * show, and `companies.reports.*` is company-scoped only
 * (`permissions.ts`'s `canSeeReportsSection`), so a coach alone gets the
 * "not allowed" state.
 */
export function ReportsPage() {
  const { t } = useTranslation();
  const { company } = useActiveCompany();
  const { permissions } = useMe();

  if (company === null) {
    return (
      <section className="page">
        <PageHeader title={t('reports.page.title')} />
        <p className="text-ink text-sm">{t('reports.page.noScope')}</p>
      </section>
    );
  }

  if (!permissions.canSeeReportsSection) {
    return (
      <section className="page flex flex-col gap-6">
        <PageHeader title={t('reports.page.title')} />
        <EmptyState text={t('reports.page.forbidden')} />
      </section>
    );
  }

  return <CompanyReports companyId={company.id} companyName={company.name} />;
}

function CompanyReports({ companyId, companyName }: { companyId: string; companyName: string }) {
  const { t } = useTranslation();
  const reports = useAsync(() => listReports(companyId), [companyId]);

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader
        title={t('reports.page.title')}
        lead={t('reports.page.lead', { company: companyName })}
      />

      <Card padding="none" aria-label={t('reports.library.title')}>
        <div className="p-5 pb-0">
          <CardHeader title={t('reports.library.title')} />
        </div>
        <AsyncSection state={reports} label={t('reports.library.title')} skeleton="card">
          {(rows) => (
            <div className="grid grid-cols-1 gap-4 p-5 pt-0 sm:grid-cols-2 lg:grid-cols-3">
              {REPORT_KEYS.map((key) => {
                const summary = rows.find((row) => row.report_key === key);
                return (
                  <ReportCard key={key} companyId={companyId} reportKey={key} summary={summary} />
                );
              })}
            </div>
          )}
        </AsyncSection>
      </Card>
    </section>
  );
}

function ReportCard({
  companyId,
  reportKey,
  summary,
}: {
  companyId: string;
  reportKey: ReportKey;
  summary: ReportSummary | undefined;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<Error | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busyFormat, setBusyFormat] = useState<'csv' | 'pdf' | null>(null);

  async function download(format: 'csv' | 'pdf') {
    setBusyFormat(format);
    setError(null);
    setNote(null);
    try {
      const result = await exportReport(companyId, reportKey, format);
      if (result.content !== undefined && result.filename !== undefined) {
        const blob = new Blob([result.content], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.filename;
        link.click();
        URL.revokeObjectURL(url);
      }
      if (format === 'pdf' && result.note !== undefined) {
        setNote(t('reports.export.pdfFallbackNote'));
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusyFormat(null);
    }
  }

  const title = t(cardTitleKey(reportKey));
  const description = t(cardDescriptionKey(reportKey));

  return (
    <Card aria-label={title}>
      <CardHeader title={title} />
      <p className="text-ink/70 -mt-2 text-[13px]">{description}</p>
      {summary !== undefined && <ReportKpis reportKey={reportKey} summary={summary} />}
      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={busyFormat !== null}
          onClick={() => void download('csv')}
        >
          {t('reports.actions.exportCsv')}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={busyFormat !== null}
          onClick={() => void download('pdf')}
        >
          {t('reports.actions.exportPdf')}
        </Button>
      </div>
      {note !== null && <p className="text-ink/70 mt-2 text-[12px]">{note}</p>}
      <SubmitError error={error} />
    </Card>
  );
}

/**
 * `reports.cards.*` keys are dotted against `t()`'s strict literal-key
 * typing (`src/i18n/react-i18next.d.ts`), but `challenge-resultaten`
 * contains a hyphen — not a valid identifier segment, so the generated
 * key-path union has no template-literal entry for it and
 * `` `reports.cards.${reportKey}.title` `` fails to typecheck. An explicit
 * switch keeps every branch a plain string literal `t()` accepts, same
 * spirit as `renderLabel.ts`'s single documented cast for data-driven keys.
 */
function cardTitleKey(reportKey: ReportKey) {
  switch (reportKey) {
    case 'vitaliteitstrends':
      return 'reports.cards.vitaliteitstrends.title' as const;
    case 'deelnamerapport':
      return 'reports.cards.deelnamerapport.title' as const;
    case 'challenge-resultaten':
      return 'reports.cards.challenge-resultaten.title' as const;
    case 'communicatiebereik':
      return 'reports.cards.communicatiebereik.title' as const;
    case 'documentgebruik':
      return 'reports.cards.documentgebruik.title' as const;
  }
}

function cardDescriptionKey(reportKey: ReportKey) {
  switch (reportKey) {
    case 'vitaliteitstrends':
      return 'reports.cards.vitaliteitstrends.description' as const;
    case 'deelnamerapport':
      return 'reports.cards.deelnamerapport.description' as const;
    case 'challenge-resultaten':
      return 'reports.cards.challenge-resultaten.description' as const;
    case 'communicatiebereik':
      return 'reports.cards.communicatiebereik.description' as const;
    case 'documentgebruik':
      return 'reports.cards.documentgebruik.description' as const;
  }
}

/** A handful of the summary's own kerncijfers, rendered generically — the
 * shape differs per report (see `php/api_reports.php`), so this reads
 * whatever numeric/string fields exist rather than special-casing each
 * report key's exact fields. */
function ReportKpis({ summary }: { reportKey: ReportKey; summary: ReportSummary }) {
  const entries = Object.entries(summary).filter(
    ([key, value]) =>
      key !== 'report_key' && (typeof value === 'number' || typeof value === 'string'),
  );

  if (entries.length === 0) {
    return null;
  }

  return (
    <dl className="mt-2 flex flex-col gap-1">
      {entries.map(([key, value]) => (
        <div key={key} className="flex justify-between text-[13px]">
          <dt className="text-ink/70">{key}</dt>
          <dd className="text-ink font-medium">{String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}
